// ImportPortfolioDialog — drag-drop CSV upload with preview + import summary.
import { useRef, useState } from 'react';
import { X, Upload, FileSpreadsheet, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import logger from '@/lib/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ImportPortfolioDialog({ clientId, onClose, onImported, onTemplate }) {
  const { token } = useAuth();
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const onPick = (f) => {
    setFile(f);
    setResult(null);
    if (!f) return;
    // Quick parse for client-side preview (first 5 lines)
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || '');
      const lines = text.split(/\r?\n/).filter(Boolean).slice(0, 6);
      const header = lines[0]?.split(',') || [];
      const rows = lines.slice(1).map((l) => l.split(','));
      setPreview({ header, rows, totalLines: text.split(/\r?\n/).filter(Boolean).length - 1 });
    };
    reader.readAsText(f);
  };

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API}/clients/${clientId}/portfolio/import`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) {
        const errText = await res.text();
        throw new Error(errText);
      }
      const body = await res.json();
      setResult(body);
      onImported?.(body);
    } catch (e) {
      logger.warn('Portfolio import failed', e);
      let msg = 'Import failed';
      try { msg = JSON.parse(e.message).detail || msg; } catch (_) { /* noop */ }
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="import-modal" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E6E0D9]">
          <h3 className="font-heading text-lg text-[#2C2420]">Import portfolio (CSV)</h3>
          <button onClick={onClose} className="text-[#5D524D] hover:text-[#2C2420]" data-testid="import-modal-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4 max-h-[75vh] overflow-y-auto">
          <div className="rounded-lg bg-[#FCFAF7] border border-[#F0EAE3] p-3 text-xs text-[#5D524D] leading-relaxed">
            Upload a CSV with the columns from our standard template (account_name, custodian, account_type, currency,
            security_name, ticker, isin, asset_class, sector, region, quantity, unit_price, market_value, cost_base, as_of_date).
            Accounts are matched by name and re-imports REPLACE existing holdings for each account.
            <button onClick={onTemplate} className="ml-1 text-[#A86A3A] underline hover:text-[#8B5730]" data-testid="import-download-template">
              Download template
            </button>
          </div>

          {/* Drop zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              const f = e.dataTransfer.files?.[0];
              if (f) onPick(f);
            }}
            onClick={() => inputRef.current?.click()}
            className={`rounded-xl border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${dragOver ? 'border-[#A86A3A] bg-[#FAF5F0]' : 'border-[#E6E0D9] bg-white hover:border-[#A86A3A]/40'}`}
            data-testid="import-dropzone"
          >
            <Upload className="w-8 h-8 text-[#A86A3A] mx-auto mb-2" />
            {file ? (
              <>
                <p className="text-sm font-medium text-[#2C2420]" data-testid="import-file-name">{file.name}</p>
                <p className="text-xs text-[#8C8078] mt-0.5">{(file.size / 1024).toFixed(1)} KB · click to replace</p>
              </>
            ) : (
              <>
                <p className="text-sm font-medium text-[#2C2420]">Drag a CSV file here, or click to browse</p>
                <p className="text-xs text-[#8C8078] mt-0.5">Single CSV up to ~5 MB</p>
              </>
            )}
            <input ref={inputRef} type="file" accept=".csv,text/csv" hidden onChange={(e) => onPick(e.target.files?.[0] || null)} data-testid="import-file-input" />
          </div>

          {/* Preview */}
          {preview && !result && (
            <div className="rounded-lg border border-[#E6E0D9] overflow-hidden" data-testid="import-preview">
              <div className="px-3 py-2 bg-[#FCFAF7] text-xs text-[#5D524D] border-b border-[#E6E0D9] flex items-center gap-2">
                <FileSpreadsheet className="w-3.5 h-3.5" />
                Preview — first {preview.rows.length} of {preview.totalLines} row{preview.totalLines === 1 ? '' : 's'}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-[#F9F7F4]">
                    <tr>{preview.header.map((h, i) => <th key={i} className="px-2 py-1.5 text-left font-medium text-[#5D524D]">{h.trim()}</th>)}</tr>
                  </thead>
                  <tbody className="divide-y divide-[#F0EAE3]">
                    {preview.rows.map((r, i) => (
                      <tr key={i}>{r.map((c, j) => <td key={j} className="px-2 py-1 text-[#2C2420]">{c}</td>)}</tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Result */}
          {result && (
            <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800 flex items-start gap-2" data-testid="import-result">
              <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium">Import complete</p>
                <p className="text-xs">
                  {result.accountsCreated} account{result.accountsCreated === 1 ? '' : 's'} created · {result.accountsTouched} touched · {result.holdingsCreated} holdings imported
                  {result.holdingsReplaced > 0 && ` · ${result.holdingsReplaced} replaced`}
                </p>
                {result.skipped?.length > 0 && (
                  <p className="text-xs mt-1 text-amber-700 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> {result.skipped.length} row{result.skipped.length === 1 ? '' : 's'} skipped (see backend log)
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-[#E6E0D9] flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} data-testid="import-modal-cancel">{result ? 'Close' : 'Cancel'}</Button>
          {!result && (
            <Button onClick={upload} disabled={!file || uploading} className="bg-[#4A3B32] hover:bg-[#3A2E26] text-white" data-testid="import-modal-upload">
              {uploading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />}
              Import
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
